import {
  getFavoriteListingIds,
  addFavoriteListing as addFavoriteListingApi,
  removeFavoriteListing as removeFavoriteListingApi,
} from '../util/api';
import { storableError } from '../util/errors';
import * as log from '../util/log';
import {
  PENDING_ACTION_FAVORITE,
  takePendingListingAction,
} from '../util/pendingListingAction';

// The signed-in member's Favourites, and the pop-ups the Share and Save
// buttons open.
//
// The saved ids live here rather than in a page duck because the same heart
// appears on the search results, My Hub and the listing page, and has to agree
// everywhere. The rows themselves are in MongoDB (server/api/misc/
// favoriteListings.js); this holds only the ids, which is all a heart needs.
//
// THE POP-UPS ARE HELD HERE TOO
//   The Save and Share buttons sit inside the listing card, which is one big
//   link. A modal rendered inside it would bubble its clicks up to that link
//   through the React tree, even from a portal. So the buttons only dispatch,
//   and one ListingActionModals, mounted by the Topbar, draws whichever pop-up
//   is open. It also lets the "saved" pop-up appear after a sign-in redirect,
//   when no button was clicked on the page at all.
//
// The whole slice is reset on logout with the rest of the store (src/reducers.js).

export const MODAL_SHARE = 'share';
export const MODAL_SAVED = 'saved';
export const MODAL_SIGN_IN = 'signIn';

// What the visitor was trying to do when the sign-in prompt opened. Decides
// where "Apply for membership" goes and what is resumed after sign-in.
export const SIGN_IN_INTENT_FAVORITE = 'favorite';
export const SIGN_IN_INTENT_ENQUIRE = 'enquire';

// ================ Action types ================ //

export const FETCH_FAVORITES_REQUEST = 'app/favoriteListings/FETCH_FAVORITES_REQUEST';
export const FETCH_FAVORITES_SUCCESS = 'app/favoriteListings/FETCH_FAVORITES_SUCCESS';
export const FETCH_FAVORITES_ERROR = 'app/favoriteListings/FETCH_FAVORITES_ERROR';

export const ADD_FAVORITE_REQUEST = 'app/favoriteListings/ADD_FAVORITE_REQUEST';
export const ADD_FAVORITE_SUCCESS = 'app/favoriteListings/ADD_FAVORITE_SUCCESS';
export const ADD_FAVORITE_ERROR = 'app/favoriteListings/ADD_FAVORITE_ERROR';

export const REMOVE_FAVORITE_REQUEST = 'app/favoriteListings/REMOVE_FAVORITE_REQUEST';
export const REMOVE_FAVORITE_SUCCESS = 'app/favoriteListings/REMOVE_FAVORITE_SUCCESS';
export const REMOVE_FAVORITE_ERROR = 'app/favoriteListings/REMOVE_FAVORITE_ERROR';

export const OPEN_LISTING_ACTION_MODAL = 'app/favoriteListings/OPEN_LISTING_ACTION_MODAL';
export const CLOSE_LISTING_ACTION_MODAL = 'app/favoriteListings/CLOSE_LISTING_ACTION_MODAL';

// ================ Reducer ================ //

const initialState = {
  favoriteIds: [],
  // Whose favourites favoriteIds holds, so the fetch is not repeated each time
  // the current user is refreshed.
  loadedForUserId: null,
  fetchInProgress: false,
  fetchError: null,
  // listingId -> true while a save or removal is on its way. Further clicks on
  // that heart are ignored until it lands.
  togglesInProgress: {},
  // null, or { type: MODAL_*, ...details }
  modal: null,
};

const withoutId = (ids, id) => ids.filter(i => i !== id);
const withId = (ids, id) => (ids.includes(id) ? ids : [id, ...ids]);
const settle = (inProgress, id) => {
  const { [id]: _, ...rest } = inProgress;
  return rest;
};

export default function favoriteListingsReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_FAVORITES_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_FAVORITES_SUCCESS: {
      // A save made while the fetch was on its way is kept, not overwritten.
      const inFlight = Object.keys(state.togglesInProgress).filter(id =>
        state.favoriteIds.includes(id)
      );
      const favoriteIds = inFlight.reduce(withId, payload.listingIds);
      return {
        ...state,
        favoriteIds,
        loadedForUserId: payload.userId,
        fetchInProgress: false,
      };
    }
    case FETCH_FAVORITES_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };

    // Saving and removing are optimistic: the heart changes on click, and is
    // put back if the server says no.
    case ADD_FAVORITE_REQUEST:
      return {
        ...state,
        favoriteIds: withId(state.favoriteIds, payload),
        togglesInProgress: { ...state.togglesInProgress, [payload]: true },
      };
    case ADD_FAVORITE_SUCCESS:
      return { ...state, togglesInProgress: settle(state.togglesInProgress, payload) };
    case ADD_FAVORITE_ERROR:
      return {
        ...state,
        favoriteIds: withoutId(state.favoriteIds, payload.listingId),
        togglesInProgress: settle(state.togglesInProgress, payload.listingId),
      };

    case REMOVE_FAVORITE_REQUEST:
      return {
        ...state,
        favoriteIds: withoutId(state.favoriteIds, payload),
        togglesInProgress: { ...state.togglesInProgress, [payload]: true },
      };
    case REMOVE_FAVORITE_SUCCESS:
      return { ...state, togglesInProgress: settle(state.togglesInProgress, payload) };
    case REMOVE_FAVORITE_ERROR:
      return {
        ...state,
        favoriteIds: withId(state.favoriteIds, payload.listingId),
        togglesInProgress: settle(state.togglesInProgress, payload.listingId),
      };

    case OPEN_LISTING_ACTION_MODAL:
      return { ...state, modal: payload };
    case CLOSE_LISTING_ACTION_MODAL:
      return { ...state, modal: null };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const isFavoriteListing = (state, listingId) =>
  !!listingId && state.favoriteListings.favoriteIds.includes(listingId);

// ================ Action creators ================ //

export const fetchFavoritesRequest = () => ({ type: FETCH_FAVORITES_REQUEST });
export const fetchFavoritesSuccess = (userId, listingIds) => ({
  type: FETCH_FAVORITES_SUCCESS,
  payload: { userId, listingIds },
});
export const fetchFavoritesError = e => ({ type: FETCH_FAVORITES_ERROR, error: true, payload: e });

export const addFavoriteRequest = listingId => ({ type: ADD_FAVORITE_REQUEST, payload: listingId });
export const addFavoriteSuccess = listingId => ({ type: ADD_FAVORITE_SUCCESS, payload: listingId });
export const addFavoriteError = (listingId, e) => ({
  type: ADD_FAVORITE_ERROR,
  error: true,
  payload: { listingId, error: e },
});

export const removeFavoriteRequest = listingId => ({
  type: REMOVE_FAVORITE_REQUEST,
  payload: listingId,
});
export const removeFavoriteSuccess = listingId => ({
  type: REMOVE_FAVORITE_SUCCESS,
  payload: listingId,
});
export const removeFavoriteError = (listingId, e) => ({
  type: REMOVE_FAVORITE_ERROR,
  error: true,
  payload: { listingId, error: e },
});

export const openShareModal = ({ url, copied }) => ({
  type: OPEN_LISTING_ACTION_MODAL,
  payload: { type: MODAL_SHARE, url, copied },
});
export const openSavedModal = listingId => ({
  type: OPEN_LISTING_ACTION_MODAL,
  payload: { type: MODAL_SAVED, listingId },
});
export const openSignInModal = ({ intent, listingId }) => ({
  type: OPEN_LISTING_ACTION_MODAL,
  payload: { type: MODAL_SIGN_IN, intent, listingId },
});
export const closeListingActionModal = () => ({ type: CLOSE_LISTING_ACTION_MODAL });

// ================ Thunks ================ //

const currentUserId = getState => getState().user.currentUser?.id?.uuid || null;

const addFavorite = listingId => dispatch => {
  dispatch(addFavoriteRequest(listingId));
  return addFavoriteListingApi(listingId)
    .then(() => {
      dispatch(addFavoriteSuccess(listingId));
      return true;
    })
    .catch(e => {
      log.error(e, 'add-favorite-listing-failed', { listingId });
      dispatch(addFavoriteError(listingId, storableError(e)));
      return false;
    });
};

const removeFavorite = listingId => dispatch => {
  dispatch(removeFavoriteRequest(listingId));
  return removeFavoriteListingApi(listingId)
    .then(() => {
      dispatch(removeFavoriteSuccess(listingId));
      return true;
    })
    .catch(e => {
      log.error(e, 'remove-favorite-listing-failed', { listingId });
      dispatch(removeFavoriteError(listingId, storableError(e)));
      return false;
    });
};

/**
 * Finish a save the visitor asked for before signing in, if there is one.
 * Shows the "saved" pop-up, as it would have after a normal click.
 */
const applyPendingFavorite = () => (dispatch, getState) => {
  const listingId = takePendingListingAction(PENDING_ACTION_FAVORITE);
  if (!listingId) {
    return Promise.resolve();
  }

  const alreadySaved = isFavoriteListing(getState(), listingId);
  const save = alreadySaved ? Promise.resolve(true) : dispatch(addFavorite(listingId));

  return save.then(saved => {
    if (saved) {
      dispatch(openSavedModal(listingId));
    }
  });
};

/**
 * Load the member's saved listing ids, then finish any save left pending by
 * the sign-in prompt. Called from the fan-out in fetchCurrentUser.
 *
 * Fire-and-forget like the other calls there: a failure leaves the hearts
 * empty, and never makes signing in look broken.
 */
export const fetchFavoriteListings = () => (dispatch, getState) => {
  const userId = currentUserId(getState);
  // The API helpers need window.fetch. The current user is not loaded during
  // server rendering today; this keeps that from breaking if it ever is.
  if (!userId || typeof window === 'undefined') {
    return Promise.resolve();
  }

  const { loadedForUserId, fetchInProgress } = getState().favoriteListings;
  if (loadedForUserId === userId || fetchInProgress) {
    return dispatch(applyPendingFavorite());
  }

  dispatch(fetchFavoritesRequest());
  return getFavoriteListingIds()
    .then(response => {
      dispatch(fetchFavoritesSuccess(userId, response?.listingIds || []));
    })
    .catch(e => {
      log.error(e, 'fetch-favorite-listings-failed');
      dispatch(fetchFavoritesError(storableError(e)));
    })
    .then(() => dispatch(applyPendingFavorite()));
};

/**
 * The Save button. Signed out, it opens the sign-in prompt instead; signed in,
 * it saves or removes, and a new save gets the "saved" pop-up.
 */
export const toggleFavoriteListing = listingId => (dispatch, getState) => {
  if (!listingId) {
    return Promise.resolve();
  }

  // The session, not the loaded profile: a member who clicks while their
  // profile is still loading is signed in, and the server only needs the
  // session cookie.
  if (!getState().auth.isAuthenticated) {
    dispatch(openSignInModal({ intent: SIGN_IN_INTENT_FAVORITE, listingId }));
    return Promise.resolve();
  }

  if (getState().favoriteListings.togglesInProgress[listingId]) {
    return Promise.resolve();
  }

  if (isFavoriteListing(getState(), listingId)) {
    return dispatch(removeFavorite(listingId));
  }

  return dispatch(addFavorite(listingId)).then(saved => {
    if (saved) {
      dispatch(openSavedModal(listingId));
    }
  });
};
